import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/use-server-fn";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Loader2, Play, RefreshCw, ArrowRight } from "lucide-react";
import { listRuns, listTemplates, startRun } from "@/features/workflows/workflow.functions";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/jobs/")({
  component: JobsIndex,
  head: () => ({
    meta: [
      { title: "Jobs · AskBell" },
      { name: "description", content: "Long-running agentic workflows and their status." },
    ],
  }),
});

const STATUS_TONE: Record<string, string> = {
  queued: "bg-muted text-muted-foreground",
  planning: "bg-ui-info-bg text-ui-info-icon border-ui-info-icon",
  running: "bg-primary/15 text-primary border-primary/30",
  retrying: "bg-ui-warning-bg text-ui-warning-icon border-ui-warning-icon",
  critiquing: "bg-ui-ai-badge-bg text-ui-ai border-ui-ai/30",
  succeeded: "bg-ui-success-bg text-ui-success-icon border-ui-success-icon",
  partial: "bg-ui-warning-bg text-ui-warning-icon border-ui-warning-icon",
  needs_review: "bg-ui-warning-bg text-ui-warning-icon border-ui-warning-icon",
  failed: "bg-destructive/15 text-destructive border-destructive/30",
  cancelled: "bg-muted text-muted-foreground",
};

function JobsIndex() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const listFn = useServerFn(listRuns);
  const tmplFn = useServerFn(listTemplates);
  const startFn = useServerFn(startRun);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [source, setSource] = useState("");
  const startRequest = useRef<{signature:string;id:string} | null>(null);

  const runsQ = useInfiniteQuery({
    queryKey: ["workflow-runs"],
    initialPageParam: undefined as {createdAt:string;id:string}|undefined,
    queryFn: ({pageParam}) => listFn({data:pageParam?{after:pageParam}:{}}),
    getNextPageParam: (page) => page.next ?? undefined,
    refetchInterval: 4000,
  });
  const tmplQ = useQuery({
    queryKey: ["workflow-templates"],
    queryFn: () => tmplFn(),
  });

  const startMutation = useMutation({
    mutationFn: async () => {
      const template = selectedTemplate;
      if (!template) throw new Error("The selected workflow template is unavailable.");
      if (!source.trim()) throw new Error("Add the source material or instructions for this job.");
      const data = {templateId:template.id,title:template.name,inputs:{source_text:source.trim()}};
      const signature = JSON.stringify(data);
      if (startRequest.current?.signature !== signature) startRequest.current = {signature,id:crypto.randomUUID()};
      const res = await startFn({data:{...data,requestId:startRequest.current.id}});
      return res;
    },
    onSuccess: (res) => {
      startRequest.current = null;
      setSelectedTemplate(null);
      toast.success("Job started");
      qc.invalidateQueries({ queryKey: ["workflow-runs"] });
      navigate({ to: "/jobs/$runId", params: { runId: res.id } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const runs = runsQ.data?.pages.flatMap(page=>page.runs) ?? [];
  const templates = (tmplQ.data?.templates ?? []).filter((t: any) => ["draft", "unproven", "proven"].includes(t.status));

  return (
    <AppLayout>
      <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="mx-auto max-w-5xl p-6 space-y-6">

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-ui-2xl font-bold">Jobs</h1>
            <p className="text-ui-base text-muted-foreground">Long-running agentic workflows.</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => runsQ.refetch()}>
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        </div>

        {templates.length > 0 && (
          <Card className="p-4">
            <div className="text-ui-base font-bold mb-2">Start a new job</div>
            <div className="flex flex-wrap gap-2">
              {templates.map((t: any) => (
                <Button
                  key={t.id}
                  variant="outline"
                  size="sm"
                  disabled={startMutation.isPending}
                  onClick={() => { startRequest.current=null; setSource(""); startMutation.reset(); setSelectedTemplate(t); }}
                >
                  {startMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                  {t.name}
                </Button>
              ))}
            </div>
          </Card>
        )}

        {tmplQ.isError && <p role="alert" className="text-ui-base text-destructive">Could not load job templates. <button className="underline" onClick={() => tmplQ.refetch()}>Try again</button></p>}
        {runsQ.isError ? (
          <Card className="p-6 text-ui-base text-destructive" role="alert">Could not load jobs. Use Refresh to try again.</Card>
        ) : runsQ.isLoading ? (
          <div className="text-ui-base text-muted-foreground">Loading…</div>
        ) : runs.length === 0 ? (
          <Card className="p-8 text-center text-ui-base text-muted-foreground">
            No jobs yet. Start one above.
          </Card>
        ) : (
          <div className="space-y-2">
            {runs.map((r: any) => (
              <Link
                key={r.id}
                to="/jobs/$runId"
                params={{ runId: r.id }}
                className="block"
              >
                <Card className="p-4 hover:border-primary/40 transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold truncate">
                          {r.title && r.title !== "Workflow run" ? r.title : "Untitled job"}
                        </span>
                        <Badge variant="outline" className={STATUS_TONE[r.status] ?? ""}>
                          {r.status}
                        </Badge>
                      </div>
                      <div className="mt-1 text-ui-sm text-muted-foreground">
                        {r.started_at
                          ? `Started ${formatDistanceToNow(new Date(r.started_at), { addSuffix: true })}`
                          : `Created ${formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}`}
                        {" · "}{r.current_step_ordinal} steps completed
                        {r.subagents
                          ? ` · ${r.subagents.done}/${r.subagents.total} sub-agents done`
                          : ""}
                        {r.total_tokens ? ` · ${r.total_tokens.toLocaleString()} tok` : ""}
                        {r.retry_count ? ` · ${r.retry_count} retries` : ""}
                      </div>

                      {r.error && (
                        <div className="mt-1 text-ui-sm text-destructive truncate">{r.error}</div>
                      )}
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </Card>
              </Link>
            ))}
            {runsQ.hasNextPage && <div className="flex justify-center pt-3"><Button variant="outline" disabled={runsQ.isFetchingNextPage} onClick={() => void runsQ.fetchNextPage()}>{runsQ.isFetchingNextPage ? <><Loader2 className="h-4 w-4 animate-spin" />Loading older jobs…</> : "Load older jobs"}</Button></div>}
          </div>
        )}
      </div>
      </div>
      <Dialog open={Boolean(selectedTemplate)} onOpenChange={(open) => { if (!open && !startMutation.isPending) setSelectedTemplate(null); }}>
        <DialogContent className="sm:max-w-xl">
          <form onSubmit={(event) => { event.preventDefault(); if (!startMutation.isPending) startMutation.mutate(); }} className="space-y-5">
            <DialogHeader>
              <DialogTitle>{selectedTemplate?.name ?? "Start a job"}</DialogTitle>
              <DialogDescription>{selectedTemplate?.description || "Add the material this workflow should use. You can follow its progress from Jobs."}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="workflow-source">Source material and instructions</Label>
              <Textarea id="workflow-source" autoFocus required maxLength={200000} value={source} onChange={(event) => setSource(event.target.value)} disabled={startMutation.isPending} className="min-h-52 resize-y" placeholder="Paste your source text and describe the result you need…" aria-describedby="workflow-source-hint" />
              <p id="workflow-source-hint" className="text-ui-sm text-muted-foreground">The job keeps running when you leave this page.</p>
            </div>
            {startMutation.isError && <p role="alert" className="text-ui-base text-destructive">{startMutation.error.message}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" disabled={startMutation.isPending} onClick={() => setSelectedTemplate(null)}>Cancel</Button>
              <Button type="submit" disabled={!source.trim() || startMutation.isPending}>{startMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}Start job</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
